<script setup lang="ts">
import { useNuxtApp } from '#app'
import { computed } from 'vue'
import BaseBox from '~/components/base/Box.vue'
import DsfrCallout from '~/components/dsfr/Callout.vue'
import DsfrTag from '~/components/dsfr/Tag.vue'
import DsfrMineursIcon from '~/components/icon/dsfr/Mineurs.vue'
import type { Mission } from '~/types/mission'

const props = defineProps<{ mission: Mission }>()

const { $filters } = useNuxtApp()

const prerequisites = computed(() => {
  return props.mission.prerequisites ? props.mission.prerequisites.filter((item) => item) : []
})

const certifications = computed(() => {
  return props.mission.certifications ?? []
})
</script>

<template>
  <BaseBox class="space-y-12">
    <div
      v-if="prerequisites.length > 0 || certifications.length > 0"
      class="border border-b-4 border-b-jva-blue-500 p-6 sm:p-8"
    >
      <div class="flex flex-wrap sm:flex-nowrap items-center gap-4 sm:gap-8 justify-start">
        <img src="/images/missions/prerequisites.svg" alt="Pré-requis" class="flex-none" />
        <div class="w-full sm:w-auto">
          <div class="text-xl font-bold mb-2">Pré-requis pour réaliser cette mission</div>
          <ol
            :class="[
              'text-[#3A3A3A] hyphens-auto',
              {
                'list-decimal pl-6': prerequisites.length + certifications.length > 1,
              },
            ]"
          >
            <li
              v-for="(prerequisite, key) in prerequisites"
              :key="key"
              class="first-letter:uppercase"
            >
              {{ prerequisite }}
            </li>
            <li v-if="certifications.length > 0">
              <span>
                {{
                  $filters.pluralize(
                    certifications.length,
                    'Certification demandée',
                    'Certifications demandées',
                    false,
                  )
                }}&nbsp;:
              </span>
              <DsfrTag
                v-for="(certification, key) in certifications"
                :key="key"
                size="sm"
                class="mr-2 my-0.5"
              >
                {{ $filters.label(certification, 'profile_certifications') }}
              </DsfrTag>
            </li>
          </ol>
        </div>
      </div>
    </div>

    <div>
      <div class="text-[22px] font-bold mb-4 text-[#161616]">Présentation de la mission</div>
      <div v-dompurify-html="mission.objectif" class="formatted-text text-[#3A3A3A]"></div>
    </div>

    <DsfrCallout v-if="mission.information">
      <template #title> Sautez le pas </template>
      <div v-dompurify-html="mission.information" />
    </DsfrCallout>

    <div>
      <div class="text-[22px] font-bold mb-4 text-[#161616]">Précisions</div>
      <div v-dompurify-html="mission.description" class="formatted-text text-[#3A3A3A]"></div>
    </div>

    <div v-if="mission.is_open_to_minors">
      <div class="text-[22px] font-bold mb-4 text-[#161616]">Mission ouverte également aux</div>
      <div class="flex items-center">
        <DsfrMineursIcon class="w-6 h-6 mr-3 text-[#3A3A3A]" aria-hidden="true" />
        <div class="text-[#3A3A3A] text-sm">Mineurs</div>
      </div>
    </div>

    <div v-if="mission.rome_macro_competences?.length">
      <div class="text-[22px] font-bold mb-4 text-[#161616]">
        {{
          $filters.pluralize(mission.rome_macro_competences.length, 'Compétence liée', 'Compétences liées', false)
        }}
      </div>
      <div class="flex flex-wrap gap-2">
        <DsfrTag v-for="(skill, key) in mission.rome_macro_competences" :key="key" size="md">
          {{ skill.libelle }}
        </DsfrTag>
      </div>
      <p class="text-[#727273] text-sm mt-4">
        {{ mission.structure?.name }} a associé ces compétences à la mission. Pas d’inquiétude : si
        vous ne les maîtrisez pas, vous pourrez les développer au cours de la mission.
      </p>
    </div>
  </BaseBox>
</template>
